import { ThumbDown, ThumbDownOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { use, useState } from 'react';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { CurrentUserContext } from '@/context/CurrentUserContext';

import { GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION } from './AssistantAiResponseFeedbackLikeButton';
import { AssistantAiResponseFeedbackCommentDialog } from './buttons/AssistantAiResponseFeedbackCommentDialog';

const ASSISTANT_AI_RESPONSE_FEEDBACK_DISLIKE_BUTTON_ITEM_FRAGMENT = graphql(`
  fragment assistantAiResponseFeedbackDislikeButtonItemFields on OrtooGptConversationItem {
    ...assistantAiResponseFeedbackCommentDialogItemFields
    _id
    response {
      responseText
    }
    feedbacksConnection {
      edges {
        node {
          ...assistantAiResponseFeedbackCommentDialogFeedbackFields
          user {
            _id
          }
          rating
          comment
        }
      }
    }
  }
`);

interface Props {
  item: DocumentType<
    typeof ASSISTANT_AI_RESPONSE_FEEDBACK_DISLIKE_BUTTON_ITEM_FRAGMENT
  >;
}

export const AssistantAiResponseFeedbackDislikeButton = ({ item }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { currentUserId } = use(CurrentUserContext);

  const { feedbacksConnection } = item ?? {};

  const feedbacks = extractConnectionProps(feedbacksConnection);

  const feedback = feedbacks?.find(
    (feedback) => feedback?.user?._id === currentUserId,
  );

  const { rating } = feedback ?? {};

  const [giveFeedbackOnConversationItem] = useMutation(
    GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION,
    {
      completionMessage:
        feedback?.rating === 'NEGATIVE'
          ? 'Disliked response'
          : 'Undisliked response',
      onCompleted(data) {
        const newFeedbacks = extractConnectionProps(
          data?.giveFeedbackOnConversationItem?.ortooGptConversationItem
            ?.feedbacksConnection,
        );

        const newFeedback = newFeedbacks?.find(
          (feedback) => feedback?.user?._id === currentUserId,
        );

        if (newFeedback?.rating === 'NEGATIVE') {
          setDialogOpen(true);
        }
      },
    },
  );

  return (
    <>
      <Tooltip
        title={
          feedback?.rating === 'NEGATIVE'
            ? 'Remove dislike'
            : `Dislike this response`
        }
      >
        <IconButton
          onClick={() => {
            if (!item._id) {
              return;
            }

            giveFeedbackOnConversationItem({
              variables: {
                params: {
                  conversationItem: item._id,
                  rating:
                    feedback?.rating === 'NEGATIVE' ? 'UNRATED' : 'NEGATIVE',
                  comment: null,
                },
              },
            });
          }}
        >
          {rating === 'NEGATIVE' ? <ThumbDown /> : <ThumbDownOutlined />}
        </IconButton>
      </Tooltip>
      <AssistantAiResponseFeedbackCommentDialog
        item={item}
        feedback={feedback}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};
